/* eslint-disable no-constant-condition */
import { COL, ROW } from "../contanst";
import { CalcRect, Cell, TableEditorConfig, Row } from "../dto";
import { generateId } from "../../utils";
import { generateCellRectsByGrid, generateGrid } from "./transform";
import { message } from "@gw/web-basic-components";

/**
 * 根据表格配置和grid，返生成新的表格配置
 * @param config - 表格配置对象
 * @param grid - 表格id grid
 * @param cellsMap - 单元格id配置映射
 * @returns 新的表格配置
 */
export const generateConfigByGrid = (
  config: TableEditorConfig,
  grid: string[][],
  cellsMap: Record<string, Cell> = {},
  initialRowConfigs: Partial<Omit<Row, "cells">>[] = []
) => {
  // 创建一个映射，将单元格ID映射到其原始配置
  const rowConfigMap = {};
  // 创建一个映射，将单元格ID映射到其原始配置
  const cellConfigMap = {
    ...cellsMap,
  };
  // 已有行的id映射
  const cellRowIdMap = {};
  // 根据grid生成单元格rectMap
  const rectsMap = generateCellRectsByGrid(grid);
  // 遍历原始配置中的所有行和列
  config.rows.forEach((row) => {
    rowConfigMap[row.id] = row;
    row.cells.forEach((cell) => {
      if (!cellConfigMap[cell.id]) {
        cellConfigMap[cell.id] = cell;
      }
      cellRowIdMap[cell.id] = row.id;
    });
  });
  // 记录已经处理的单元格
  const processedCells = new Set();
  // 新行
  const newRows = Array.from<TableEditorConfig["rows"][number]>({
    length: grid.length,
  });
  for (let i = 0; i < grid.length; i++) {
    // 行id（如果是原有就不变否则新生成一个id）
    let rowId;
    const row: Cell[] = [];
    for (let j = 0; j < grid[i].length; j++) {
      const cellId = grid[i][j];
      const oldCell = cellConfigMap[cellId];
      const { minRow, minCol, maxRow, maxCol } = rectsMap[cellId];
      const processed = processedCells.has(cellId);
      // 对应id处理过就跳过
      if (processed) {
        continue;
      }
      // 记录原有rowId
      if (!rowId && cellRowIdMap[cellId]) {
        rowId = cellRowIdMap[cellId];
      }
      // 跨行跨列情况
      const colSpan = maxCol - minCol + 1;
      const rowSpan = maxRow - minRow + 1;
      // 如果是已经存在的单元格则更新跨行跨列否则新生成一个单元格配置
      if (oldCell) {
        row.push({
          ...oldCell,
          colSpan,
          rowSpan,
          rect: rectsMap[cellId],
        });
      } else {
        row.push({
          id: cellId,
          colSpan,
          rowSpan,
          rect: rectsMap[cellId],
        });
      }
      processedCells.add(cellId);
    }
    const initialRow = initialRowConfigs[i] ?? {};
    if (rowId) {
      newRows[i] = { ...rowConfigMap[rowId], ...initialRow, cells: row };
    } else {
      newRows[i] = {
        id: generateId(),
        ...initialRow,
        cells: row,
        height: ROW.DEFAAULT_HEIGHT,
      };
    }
  }
  return {
    ...config,
    rows: newRows,
  };
};

/**
 * 合并单元格
 *
 * @param config - 表格对象
 * @param cellIds - 要合并的单元格id
 * @returns 合并后的表格对象
 */
export const mergeCells = (
  config: TableEditorConfig,
  rect: CalcRect
): { config: TableEditorConfig; mergeId?: string } => {
  const grid = generateGrid(config);
  const { minRow, minCol, maxRow, maxCol } = rect;
  // 取第一个id为合并后的id
  const mergeId = grid[minRow][minCol];
  for (let i = minRow; i <= maxRow; i++) {
    for (let j = minCol; j <= maxCol; j++) {
      grid[i][j] = mergeId;
    }
  }
  return { config: generateConfigByGrid(config, grid), mergeId };
};

/**
 * 拆分单元格
 *
 * @param config - 表格对象
 * @param cellId - 要拆分的单元格id
 * @returns 分割后的表格对象
 */
export const splitCell = (config: TableEditorConfig, cellId: string) => {
  const grid = generateGrid(config);
  const { minRow, minCol, maxRow, maxCol } =
    generateCellRectsByGrid(grid)[cellId];
  const cellsMap = {};
  // 如果已经是最小单元格
  if (maxRow - minRow === 0 && maxCol - minCol === 0) {
    return {
      config,
      cellIds: [],
    };
  }
  const cellIds: string[] = [];
  for (let i = minRow; i <= maxRow; i++) {
    for (let j = minCol; j <= maxCol; j++) {
      // 第一个单元格保留原id
      if (i === minRow && j === minCol) {
        continue;
      }
      const id = generateId();
      grid[i][j] = id;
      cellIds.push(id);
      cellsMap[id] = { id };
    }
  }
  return {
    config: generateConfigByGrid(config, grid, cellsMap),
    cellIds,
  };
};
/**
 * 插入行
 * @param config - 表格对象
 * @param cellId - 要插入行的单元格id
 * @param direction - 插入位置
 * @returns - 插入后的表格对象&新行id
 */
export const insertRow = (
  config: TableEditorConfig,
  cellId: string,
  direction: "before" | "after"
) => {
  const grid = generateGrid(config);
  const { minRow, maxRow } = generateCellRectsByGrid(grid)[cellId];
  // 新行index
  const idx = direction === "before" ? minRow : maxRow + 1;
  const row = [];
  for (let i = 0; i < config.cols.length; i++) {
    let id: string;
    // 如果新增的单元格存在跨行中间的情况则id顺延
    if (grid[idx]?.[i] === grid[idx - 1]?.[i]) {
      id = grid[idx][i];
    } else {
      id = generateId();
    }
    row.push(id);
  }
  grid.splice(idx, 0, row);
  const newConfig = generateConfigByGrid(config, grid);
  // 更新循环rect配置
  newConfig.loops = newConfig.loops?.map((l) => {
    let { minRow: loopMinRow, maxRow: loopMaxRow } = l;
    // 如果新增行在循环中间或上方则需要更新循环的rect
    if (loopMinRow >= idx) {
      loopMinRow++;
      loopMaxRow++;
    } else if (idx <= loopMaxRow) {
      loopMaxRow++;
    }
    return { ...l, minRow: loopMinRow, maxRow: loopMaxRow };
  });
  return newConfig;
};

/**
 * 插入列
 * @param config - 表格对象
 * @param cellId - 要插入列的单元格id
 * @param direction - 插入位置
 * @returns - 插入后的表格对象
 */
export const insertCol = (
  config: TableEditorConfig,
  cellId: string,
  direction: "before" | "after"
) => {
  const grid = generateGrid(config);
  const newCols = config.cols.slice();
  const { minCol, maxCol } = generateCellRectsByGrid(grid)[cellId];
  // 新列index
  const idx = direction === "before" ? minCol : maxCol + 1;
  newCols.splice(idx, 0, COL.DEFAAULT_WIDTH);
  for (let i = 0; i < grid.length; i++) {
    // 如果新增的单元格存在跨列中间的情况则id顺延
    if (grid[i]?.[idx] === grid[i]?.[idx - 1]) {
      grid[i].splice(idx, 0, grid[i][idx]);
    } else {
      grid[i].splice(idx, 0, generateId());
    }
  }
  const newConfig = generateConfigByGrid(config, grid);
  newConfig.cols = newCols;
  return newConfig;
};

/**
 * 删除行
 * @param config - 表格对象
 * @param cellIds - 要删除行的单元格id
 * @returns - 删除后的表格对象
 */
export const deleteRows = (config: TableEditorConfig, cellIds: string[]) => {
  const grid = generateGrid(config);
  const rects = generateCellRectsByGrid(grid);
  // 标记需要删除的行
  const markRows = new Set<number>();
  for (const cellId of cellIds) {
    const { minRow, maxRow } = rects[cellId];
    for (let i = minRow; i <= maxRow; i++) {
      markRows.add(i);
    }
  }
  const newGrid: string[][] = [];
  for (let i = 0; i < grid.length; i++) {
    if (!markRows.has(i)) {
      newGrid.push(grid[i]);
    }
  }
  if (newGrid.length === 0) {
    message.error("请至少保留一行");
    return config;
  }
  const newConfig = generateConfigByGrid(config, newGrid);
  // 更新循环rect配置 如过循环的列都被删除则过滤掉
  newConfig.loops = newConfig.loops
    ?.filter(
      (l) =>
        !Array.from({ length: l.maxRow - l.minRow + 1 })
          .map((_, idx) => idx + l.minRow)
          .every((i) => markRows.has(i))
    )
    .map((l) => {
      const { minRow: loopMinRow, maxRow: loopMaxRow } = l;
      // 如果新增行在循环中间或上方则需要更新循环的rect（统计数量）
      let max = 0,
        all = 0;
      markRows.forEach((idx) => {
        if (loopMinRow > idx) {
          all++;
        } else if (idx <= loopMaxRow) {
          max++;
        }
      });
      return {
        ...l,
        minRow: loopMinRow - all,
        maxRow: loopMaxRow - all - max,
      };
    });
  return newConfig;
};
/**
 * 删除列
 * @param config - 表格对象
 * @param cellIds - 要删除列的单元格id
 * @returns - 删除后的表格对象
 */
export const deleteCols = (config: TableEditorConfig, cellIds: string[]) => {
  const grid = generateGrid(config);
  const rects = generateCellRectsByGrid(grid);
  // 标记需要删除的行
  const markCols = new Set<number>();
  for (const cellId of cellIds) {
    const { minCol, maxCol } = rects[cellId];
    for (let i = minCol; i <= maxCol; i++) {
      markCols.add(i);
    }
  }
  const newGrid: string[][] = [],
    newCols: number[] = [];
  for (let i = 0; i < grid.length; i++) {
    newGrid[i] = [];
    for (let j = 0; j < grid[i].length; j++) {
      if (!markCols.has(j)) {
        newGrid[i].push(grid[i][j]);
        if (i === 0) {
          newCols.push(config.cols[j]);
        }
      }
    }
  }
  if (newCols.length === 0) {
    message.error("请至少保留一列");
    return config;
  }
  const newConfig = generateConfigByGrid(config, newGrid);
  newConfig.cols = newCols;
  return newConfig;
};
